import { ChangeDetectionStrategy, Component, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CustomNodeComponent, HandleComponent, SelectableDirective } from 'ngx-vflow';
import { WorkflowNodeType } from '../../../../../models/workflow-builder.model';
import {
  ORPHAN_HANDLE_ID,
  TARGET_HANDLE_ID,
  WorkflowBranchHandle,
  getWorkflowNodeTypeMeta
} from '../../../../models/node-catalog';

/**
 * Datos que el canvas inyecta en cada tarjeta. Los DERIVA el store (canvas → toCardData),
 * no la propia tarjeta: la tarjeta solo pinta, para respetar OnPush + reactividad por
 * signals del store (gotcha §6, "nada de estado mutado a mano").
 */
export interface WorkflowNodeCardData {
  nodeKey: string;
  nodeType: WorkflowNodeType;
  title: string;
  /** Resumen legible de la config (subtipo/descripción). */
  summary: string;
  /** Un puerto de salida por rama (PUERTO = branchKey). */
  handles: WorkflowBranchHandle[];
  /** Ramas del store que ya no son puerto real del nodo: se dibujan como huérfanas. */
  orphanBranchKeys: string[];
  /** Alguna arista apunta a este nodo (el TRIGGER solo dibuja entrada si la tiene). */
  hasIncoming: boolean;
  /** Tiene errores de validación de nodo → badge ámbar "Incompleto". */
  incomplete: boolean;
  isActive: boolean;
}

/**
 * Tarjeta de un nodo en el canvas de workflow.
 *
 * Cada puerto de salida se renderiza como una fila con su propio `<handle>`: arrastrar
 * desde ahí crea la arista con el `branchKey` de ESE puerto — sin ningún `<select>` de
 * rama (el builder viejo lo pedía en un modal). Los iconos son de fuente (FontAwesome vía
 * clase), NO strings `innerHTML` SVG (corrige la brecha UI-7).
 */
@Component({
  selector: 'app-workflow-node-card',
  standalone: true,
  imports: [CommonModule, HandleComponent, SelectableDirective],
  templateUrl: './node-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkflowNodeCardComponent extends CustomNodeComponent<WorkflowNodeCardData> {
  protected readonly targetHandleId = TARGET_HANDLE_ID;
  protected readonly orphanHandleId = ORPHAN_HANDLE_ID;

  protected readonly meta = computed(() => {
    const type = this.data()?.nodeType;
    return type ? getWorkflowNodeTypeMeta(type) : null;
  });

  /**
   * El TRIGGER es el punto de entrada: no admite conexiones entrantes (el validador las
   * rechaza), así que no muestra puerto de entrada salvo que un grafo heredado lo apunte.
   */
  protected readonly showTargetHandle = computed(() => {
    const data = this.data();
    return !!data && (data.nodeType !== 'TRIGGER' || data.hasIncoming);
  });

  protected readonly displayTitle = computed(() => {
    const data = this.data();
    if (!data) {
      return '';
    }
    return data.title.trim() || getWorkflowNodeTypeMeta(data.nodeType).label;
  });

  protected trackHandle(_index: number, handle: WorkflowBranchHandle): string {
    return handle.branchKey;
  }
}
